import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Stack,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import {
  Color,
  FontStyle,
  defaultBottomPadding,
  defaultInnerStyle,
  defaultContainerStyle,
  ApplicationInputStyle,
  SurveyButtonStyle,
  Inputs,
  ToolbarStyle,
} from "../../constants";
import CheckButton from "../CheckButton";
import HeartButton from "../HeartButton";
import TempDisplay from "../TempDisplay";
import { CustomPaper, StyledTextField } from "../AutocompleteUtils";
import { useDispatch, useSelector } from "react-redux";
import * as apiService from "../../service/ApiService";
import { isLoggedIn } from "../../utils/utils";
import FettiFileUpload from "../FettiFileUpload";
import FettiInput from "../FettiInput";
import ResumeSelect from "../ApplyPanels/ResumeSelect";
import NewResumeButton from "../ApplyPanels/NewResumeButton";
import ResumePreview from "../ResumeComponents/ResumePreview";
// import GoogleDriveFileUpload, {
//   GoogleDriveStatus,
// } from "../GoogleDriveFileUpload";
const GoogleDriveStatus = {
  // a temp since we are phasing out google drive
  IDLE: "IDLE",
  DOWNLOADING: "Fetching file from Google Drive...",
  SAVING: "Saving...",
  DONE: "Saved!",
};

const styles = {
  container: {
    ...defaultContainerStyle,
  },
  inner: {
    ...defaultInnerStyle,
    "& .MuiFormControl-root": {
      border: "none",
    },
    px: { xs: "0.5rem", sm: 0 },
    paddingBottom: "16px",
  },
  subtitle: {
    ...FontStyle.body,
    fontSize: "1rem",
    textAlign: "center",
  },
  link: {
    ...FontStyle.body,
    fontSize: "1rem",
    textAlign: "center",
    textDecoration: "underline",
    cursor: "pointer",
    color: "black",
  },
  body: {
    ...FontStyle.body,
    fontSize: "1rem",
    textAlign: "left",
    lineHeight: "1.4",
    maxWidth: "500px",
    "& > * >a": {
      color: "#000",
      textDecoration: "none",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
  label: {
    ...FontStyle.headingBody,
    fontSize: "1rem",
    // textAlign: 'center',
    lineHeight: "1.4",
    // width: "100%",
    maxWidth: "500px",
  },
  search: {
    width: "100%",
    backgroundColor: "rgb(255, 255, 255)",
    borderRadius: "4px",
    border: "1px solid rgb(204, 204, 204)",
  },
  searchProps: {
    disableUnderline: true,
  },
  info: {
    borderLeft: `6px solid ${Color.primary}`,
    borderRadius: "0.75rem",
    backgroundColor: "#fff",
    padding: "1.2rem",
  },
};

const Q_Resume_Upload = ({
  selected,
  setSelected,
  setQuestionsDone,
  questionNum,
  currentQuestionNum,
  options,
  setRefreshCount,
}) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const apply = useSelector((state) => state.apply);
  const loggedIn = isLoggedIn(auth);
  const resumeList = apply.serverData.resumes;
  const selectedResume = apply.app.resume;
  const [loadedResumes, setLoadedResumes] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [file, setFile] = React.useState({
    data: null,
    name: "",
    size: 0,
  });
  const [uploadName, setUploadName] = React.useState("");
  const [userOpenedUploadUI, setUserOpenedUploadUI] = React.useState(false);
  const showUploadUI =
    userOpenedUploadUI || apply.serverData.resumes.length === 0;

  const [googleDriveStatus, setGoogleDriveStatus] = useState(
    GoogleDriveStatus.IDLE
  );

  const serverRefreshes = useSelector((state) => state.survey.serverRefreshes);

  useEffect(() => {
    setRefreshCount((c) => c + 1);
  }, [userOpenedUploadUI, resumeList, file, apply.resumeUrls]);

  useEffect(() => {
    // load resume list when entering this page
    if (!loggedIn) return;
    if (loadedResumes) return;
    if (currentQuestionNum !== questionNum) return;
    console.log("loading job data", resumeList);
    apiService.getResumeUploads2(
      dispatch,
      { ...auth.accountData },
      {
        handleSuccess: () => {
          setLoadedResumes(true);
        },
      }
    );
  }, [loggedIn, loadedResumes, questionNum, currentQuestionNum]);

  useEffect(() => {
    // set the default file name when a file is uploaded
    if (file.data && file.name) {
      setUploadName(file.name.split(".")[0]);
    }
  }, [file]);
  useEffect(() => {
    // clear google drive status if upload UI is closed
    if (!userOpenedUploadUI) {
      setGoogleDriveStatus(GoogleDriveStatus.IDLE);
    }
  }, [userOpenedUploadUI]);

  const handleUpload = () => {
    const fileName = uploadName;
    const filePath = file.name;
    const fileData = file.data;
    setUploading(true);
    apiService.uploadResume(
      dispatch,
      {
        ...auth.accountData,
        fileName,
        filePath,
        fileData,
      },
      {
        handleFinish: () => {
          setUploading(false);
        },
        handleSuccess: () => {
          setFile({
            data: null,
            name: "",
            size: 0,
          });
          setUploadName("");
          setUserOpenedUploadUI(false);
        },
      }
    );
  };

  // set done if user has an uploaded resume
  useEffect(() => {
    if (selectedResume) {
      setQuestionsDone((prev) => ({ ...prev, [questionNum]: true }));
    } else {
      setQuestionsDone((prev) => ({ ...prev, [questionNum]: false }));
    }
  }, [selectedResume]);
  return (
    <Stack
      direction="column"
      sx={styles.container}
      alignItems="center"
      spacing={2}>
      {/* <TempDisplay selected={selected} options={options} questionNum={questionNum} /> */}
      {!loggedIn && (
        // this shouldn't happen
        <Box sx={styles.subtitle}>Please log in to upload a resume</Box>
      )}
      {loggedIn && !loadedResumes && <CircularProgress />}
      {loggedIn && loadedResumes && (
        <Stack
          direction="column"
          sx={styles.inner}
          alignItems="left"
          justifyContent="center"
          spacing={2}>
          {resumeList.length > 0 && (
            <Stack direction="column" sx={{ width: "100%" }} spacing={1.5}>
              <ResumeSelect headerStyle={styles.label} />
              {!showUploadUI && (
                <NewResumeButton setOpen={setUserOpenedUploadUI} shadow />
              )}
            </Stack>
          )}
          {resumeList.length > 0 && !showUploadUI && (
            <ResumePreview
              resumeId={selectedResume[0]}
              fileType={selectedResume[2]}
            />
          )}
          {showUploadUI && (
            <>
              {googleDriveStatus === GoogleDriveStatus.IDLE && (
                <FettiFileUpload
                  file={file}
                  setFile={setFile}
                  label="Resume"
                  canClose={resumeList.length > 0}
                  onClose={() => setUserOpenedUploadUI(false)}
                />
              )}
              {!file.data && (
                <Stack direction="row" sx={{ width: "100%" }} spacing={2}>
                  {/* <GoogleDriveFileUpload
                    status={googleDriveStatus}
                    setStatus={setGoogleDriveStatus}
                    handleFinishUpload={() => setUserOpenedUploadUI(false)}
                  /> */}
                  {resumeList.length > 0 &&
                    googleDriveStatus === GoogleDriveStatus.IDLE && (
                      <Stack
                        {...ToolbarStyle.stackProps}
                        sx={{ ...ToolbarStyle.stackStyle, px: "16px" }}
                        onClick={() => setUserOpenedUploadUI(false)}>
                        Cancel
                      </Stack>
                    )}
                </Stack>
              )}
              {file.data && (
                <Stack direction="column" spacing={1}>
                  <Box sx={ApplicationInputStyle.header}>
                    Name your resume (for your reference)
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Stack
                      {...ApplicationInputStyle.stackProps}
                      sx={ApplicationInputStyle.stack}>
                      <FettiInput
                        value={uploadName}
                        placeholder="Resume (Full-Stack Developer)"
                        onChange={(v) => setUploadName(v.target.value)}
                        maxLength={50}
                      />
                    </Stack>
                    <Button
                      sx={{
                        ...SurveyButtonStyle.defaultStyling,
                        ...SurveyButtonStyle.blueFill,
                        borderRadius: Inputs.borderRadius,
                        width: "100px",
                        minWidth: "100px",
                        height: "39px",
                      }}
                      disableRipple
                      fullWidth
                      onClick={handleUpload}
                      disabled={uploading}>
                      {uploading ? <CircularProgress size="20px" /> : "Upload"}
                    </Button>
                  </Stack>
                </Stack>
              )}
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default Q_Resume_Upload;
